<script setup lang="ts">
import { computed, h, type VNode } from 'vue';
import { NIcon, type DropdownOption } from 'naive-ui';
import { IconMore, IconUser } from '@/components/design/icons';
import { isOfficeAddinHost } from '@/office/utils';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaUploadButton from '@/components/design/base/GaiaUploadButton.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { describeVaultCounts } from '@/utils/vaultSelection';

const props = withDefaults(
  defineProps<{
    hasSelectedItems: boolean;
    selectedCount: number;
    /** Number of selected files (for bulk reassign owner) */
    selectedFileCount?: number;
    /** Number of selected folders (for bulk reassign owner; backend includes all files inside) */
    selectedFolderCount?: number;
    /** Number of selected contracts (for bulk reassign owner) */
    selectedContractCount?: number;
    /** Whether at least one file or folder is selected for reassign (org admin only) */
    hasSelectionForReassign?: boolean;
    /** Whether current user is org admin (bulk reassign + unassign) */
    isOrgAdmin?: boolean;
    compact?: boolean;
    /** Icon-only toolbar buttons with a tooltip instead of a text label. */
    iconOnly?: boolean;
    /** Upload files / upload folder buttons. */
    showUpload?: boolean;
    /** "New Folder" button. */
    showCreateFolder?: boolean;
    /**
     * Why nothing can be created in the current folder (no WRITE on it); the
     * upload and new-folder entries stay, disabled with this reason.
     */
    createBlockedReason?: string | null;
    /** Bulk delete button. */
    showDelete?: boolean;
    /** Bulk reassign owner button. */
    showReassignOwner?: boolean;
  }>(),
  {
    selectedFileCount: 0,
    selectedFolderCount: 0,
    selectedContractCount: 0,
    hasSelectionForReassign: false,
    isOrgAdmin: false,
    compact: false,
    iconOnly: false,
    showUpload: true,
    showCreateFolder: true,
    createBlockedReason: null,
    showDelete: true,
    showReassignOwner: true,
  }
);

const reassignLabel = computed(() =>
  describeVaultCounts({
    files: props.selectedFileCount,
    contracts: props.selectedContractCount,
    folders: props.selectedFolderCount,
  })
);

const emit = defineEmits<{
  'create-folder': [];
  upload: [];
  'upload-folder': [];
  'bulk-delete': [];
  'reassign-owner': [];
  'upload-current-doc': [];
}>();

// Shared "create" entries: the standard layout shows them under the "Upload"
// dropdown, the office layout under its "⋯" dropdown — the same set on both
// surfaces. Filtered by the caller's show* config so embedded hosts can trim them.
const allCreateOptions = computed<DropdownOption[]>(() => [
  {
    label: 'New Folder',
    key: 'create-folder',
    icon: (): VNode =>
      h(NIcon, null, {
        default: () =>
          h(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '24',
              height: '24',
              viewBox: '0 0 24 24',
            },
            [
              h('path', {
                fill: 'currentColor',
                d: 'M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z',
              }),
              h('path', {
                fill: 'currentColor',
                d: 'M12 14h2v4h-2zm0-3h2v2h-2zm4 3h2v4h-2zm0-3h2v2h-2z',
              }),
            ]
          ),
      }),
  },
  {
    label: 'Upload Files',
    key: 'upload',
    icon: (): VNode =>
      h(NIcon, null, {
        default: () =>
          h(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '24',
              height: '24',
              viewBox: '0 0 24 24',
            },
            [h('path', { fill: 'currentColor', d: 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z' })]
          ),
      }),
  },
  {
    label: 'Upload Folder',
    key: 'upload-folder',
    icon: (): VNode =>
      h(NIcon, null, {
        default: () =>
          h(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '24',
              height: '24',
              viewBox: '0 0 24 24',
            },
            [
              h('path', {
                fill: 'currentColor',
                d: 'M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z',
              }),
              h('path', { fill: 'currentColor', d: 'M12 10l-4 4h3v4h2v-4h3l-4-4z' }),
            ]
          ),
      }),
  },
]);

const createOptions = computed<DropdownOption[]>(() =>
  allCreateOptions.value
    .filter(option => {
      if (option.key === 'create-folder') return props.showCreateFolder;
      if (option.key === 'upload' || option.key === 'upload-folder') return props.showUpload;
      return true;
    })
    .map(option =>
      props.createBlockedReason
        ? { ...option, disabled: true, props: { title: props.createBlockedReason } }
        : option
    )
);

function handleDropdownSelect(key: string): void {
  switch (key) {
    case 'create-folder': {
      emit('create-folder');
      break;
    }
    case 'upload': {
      emit('upload');
      break;
    }
    case 'upload-folder': {
      emit('upload-folder');
      break;
    }
    // No default
  }
}

function getCompactActionsMenuProps(): { class: string } {
  return {
    class: 'file-vault-compact-actions-menu',
  };
}
</script>

<template>
  <div class="actions" :class="{ compact: props.compact }">
    <!-- Word task pane: the one action worth a button of its own is uploading
         the document you already have open; the rest fold into a menu. -->
    <template v-if="isOfficeAddinHost()">
      <div class="office-row">
        <gaia-upload-button
          v-if="props.showUpload"
          subject="current document"
          size="sm"
          :disabled="props.createBlockedReason !== null"
          :title="props.createBlockedReason ?? undefined"
          @click="emit('upload-current-doc')"
        />

        <n-flex align="center" :size="6" :wrap="false">
          <gaia-button
            v-if="props.showReassignOwner && props.isOrgAdmin && props.hasSelectionForReassign"
            size="small"
            @click="emit('reassign-owner')"
          >
            <template #icon>
              <n-icon :component="IconUser" />
            </template>
            Owner ({{ reassignLabel }})
          </gaia-button>

          <gaia-button
            v-if="props.showDelete && props.selectedCount > 1"
            type="error"
            size="small"
            @click="emit('bulk-delete')"
          >
            Delete ({{ props.selectedCount }})
          </gaia-button>

          <gaia-dropdown
            :options="createOptions"
            :menu-props="getCompactActionsMenuProps"
            placement="bottom-end"
            @select="handleDropdownSelect"
          >
            <gaia-icon-button :dimension="28" title="More actions">
              <n-icon :component="IconMore" :size="14" />
            </gaia-icon-button>
          </gaia-dropdown>
        </n-flex>
      </div>
    </template>

    <!-- Standard layout: the "Upload" menu of the same create entries — the
         arrow rather than the plus, since uploading is what it is mostly for. -->
    <template v-else>
      <gaia-tooltip v-if="createOptions.length > 0" :disabled="!props.iconOnly">
        <!-- The span gives the tooltip an element to bind to; the dropdown
             component has none of its own. -->
        <template #trigger>
          <span>
            <gaia-dropdown
              :options="createOptions"
              placement="bottom-end"
              @select="handleDropdownSelect"
            >
              <!-- Slotted label: the trigger opens a menu of things to create,
                 and the menu names each one. Collapses to the glyph when the
                 task pane is too narrow for a label. -->
              <gaia-upload-button :size="props.iconOnly ? 'sm' : 'md'" data-tour="vault.new-button">
                <!-- The slot is always provided: withholding it would fall back to
                   the composed "Upload" label instead of leaving the glyph bare. -->
                {{ props.iconOnly ? '' : 'Upload' }}
              </gaia-upload-button>
            </gaia-dropdown>
          </span>
        </template>
        Upload
      </gaia-tooltip>
    </template>
  </div>
</template>

<style scoped>
.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.actions.compact {
  gap: 8px;
}

.office-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;
}
</style>
